import { createContext, useContext, useEffect, useMemo, useState } from "react";

const dictionaries = {
  cs: {
    nav: {
      links: [
        { label: "Práce", href: "#work" },
        { label: "Služby", href: "#services" },
        { label: "Technologie", href: "#tech" },
        { label: "Ceník", href: "#pricing" },
        { label: "Proces", href: "#process" },
        { label: "Studio", href: "#founder" },
      ],
      cta: "Začít projekt",
      studios: "Studios",
      langLabel: "Jazyk",
    },
    hero: {
      eyebrow: "Studio z Česka — od 2025",
      headlinePre: "Stavíme",
      headlineRotating: [
        "weby",
        "webové aplikace",
        "SaaS produkty",
        "AI systémy",
        "automatizace",
      ],
      headlineLines: ["pro firmy, které", "berou byznys vážně."],
      sub: "Boutique software studio z Česka. Pracujete přímo se zakladatelem — od první zprávy po launch a další rozvoj. Žádní account manažeři, žádné prostředníky.",
      ctaPrimary: "Začít projekt",
      ctaSecondary: "Naše práce",
      scroll: "Scroll",
      floatBooking: "Aktuálně přijímáme nové projekty · Q3 2026",
      floatStat: { value: "do 24 h", label: "odpovídáme na poptávky" },
      pricePill: "Ceny od 4 000 Kč",
    },
    trust: {
      items: [
        "Sídlo v Česku",
        "Přímá komunikace",
        "Bez subdodávek",
        "Moderní technologie",
        "Dlouhodobá podpora",
      ],
    },
    stats: {
      eyebrow: "Krátce",
      heading: "Design, vývoj a automatizace pod jednou střechou.",
      sub: "Rychlá komunikace, čistý kód a funkční řešení. Bez prostředníků, bez agenturních nesmyslů.",
      items: [
        { value: 2025, suffix: "", label: "Rok založení", sub: "Studio z Česka" },
        { value: 24, suffix: "h", label: "Odpovídáme do", sub: "Po–Pá, e-mail / Slack" },
        { value: 6, suffix: "", label: "Oborů pod jednou střechou", sub: "Web · App · AI · Branding · Motion" },
        { value: 100, suffix: "%", label: "Na míru, bez šablon", sub: "Žádné subdodávky" },
      ],
    },
    why: {
      eyebrow: "Proč s námi?",
      index: "(Parabolum® — 01)",
      annotation: "— proč Parabolum.",
      items: [
        {
          n: "01",
          statement: "Stavíme weby, aplikace a systémy tak, aby v nich uživatelé našli to, co hledají — bez tření.",
          result: "Více konverzí, méně podpory pro běžné dotazy a digitální produkt, který reálně pracuje pro vás.",
          outcome: "Reálný obchodní dopad",
        },
        {
          n: "02",
          statement: "Pracujeme s moderním technologickým stackem a čistou architekturou — žádné šablony, žádné technické dluhy.",
          result: "Rychlejší dodávání nových funkcí, nižší náklady na údržbu a kód, který přežije roky používání.",
          outcome: "Udržitelná technologie",
        },
        {
          n: "03",
          statement: "Komunikujete přímo se zakladatelem. Bez prostředníků, bez account manažerů, bez nekonečných meetingů.",
          result: "Rychlejší rozhodování, férová cena a partnerství, ve kterém má každá zpráva smysl.",
          outcome: "Přímá spolupráce",
        },
      ],
    },
    clients: {
      eyebrow: "Vybrané projekty",
      headingPre: "Tři projekty, ",
      headingItalic: "na kterých",
      headingPost: " jsme nedávno pracovali.",
      sub: "Studiové i klientské projekty, na kterých si můžete prohlédnout, jak přemýšlíme o produktu, technologii a detailech.",
      logos: [
        "LeadForge",
        "Revix",
        "BitWrex",
        "Parabolum Studio",
      ],
    },
    work: {
      eyebrow: "Vybrané projekty — 2025/2026",
      heading: ["Případové studie ", "na míru", "."],
      archive: "Plný archiv brzy",
      inMotion: "V pohybu",
      dashboard: "Dashboard",
      dashAmount: "284 720 Kč",
      thisWeek: "+24,7 % tento týden",
      project: "Projekt",
      labels: {
        challenge: "Výzva",
        solution: "Řešení",
        tech: "Technologie",
        outcome: "Výsledek",
      },
      items: [
        {
          tag: "Lead Generation · SaaS",
          title: "LeadForge",
          year: "2026",
          desc: "Platforma pro generování a kvalifikaci B2B leadů s AI obohacováním dat.",
          challenge: "Obchodní týmy ztrácejí hodiny denně ručním hledáním a kvalifikací kontaktů v Excelu a LinkedInu. Data jsou roztříštěná, zastaralá a kvalifikace probíhá subjektivně.",
          solution: "Postavili jsme webovou platformu, která importuje zdroje leadů, automaticky je obohacuje pomocí AI o firmografická data a podle pravidel vyhodnocuje, který kontakt má smysl oslovit jako první.",
          tech: ["React", "FastAPI", "MongoDB", "OpenAI API", "Vector search"],
          outcome: "Místo manuálního prohledávání má obchodník seřazený seznam priorit a může se věnovat samotnému oslovení. Funkční verze nasazená a v denním používání.",
        },
        {
          tag: "Automotive · E-commerce",
          title: "REVIX",
          year: "2026",
          desc: "Admin dashboard a storefront pro performance / automotive segment.",
          challenge: "Performance a tuning shopy stále řeší den mezi tabulkami, výpisy z marketplace a DM zprávami. Neexistuje back-office, který by sedl tomu, jak tohle odvětví reálně funguje — díly s kompatibilitou na konkrétní auta, zakázkové buildy, zákazník, který čeká, že prodejna ví, co se mu na auto hodí.",
          solution: "Postavili jsme admin dashboard a zákaznický storefront pro performance automotive. Katalog s metadaty kompatibility s vozidly, správa objednávek a poptávek, profily zákazníků a back-office, ze kterého je možné prodejnu reálně řídit — místo skládání pěti různých nástrojů.",
          tech: ["React", "FastAPI", "MongoDB", "Python", "Stripe", "Tailwind"],
          outcome: "Koncept v aktivním vývoji. Storefront + admin v jednom systému, postavený s důrazem na reálný workflow tuningové prodejny.",
        },
        {
          tag: "Crypto · Exchange",
          title: "BitWrex",
          year: "2025",
          desc: "Frontend a infrastruktura pro krypto směnárnu zaměřenou na rychlou a jednoduchou výměnu.",
          challenge: "Většina krypto směnáren je pro běžného uživatele zbytečně komplikovaná — graf, deriváty, leverage. Cílem bylo vytvořit rozhraní, kde proběhne výměna ve třech krocích.",
          solution: "Navrhli jsme a postavili čistý web s minimálním rozhraním pro výměnu, propojený s vlastní backendovou vrstvou pro kurzy, KYC a zpracování transakcí.",
          tech: ["React", "FastAPI", "Python", "MongoDB", "Stripe", "API integrace"],
          outcome: "Směnárna nasazená v produkci s důrazem na rychlost UX a srozumitelný onboarding. Pokračujeme v dlouhodobém rozvoji a podpoře.",
        },
      ],
    },
    services: {
      eyebrow: "Služby",
      heading: ["Co pro vás ", "stavíme."],
      sub: "Specializujeme se na software, AI a automatizace. Design a branding jsou součást procesu, ale produkt vždy stojí na funkční technologii.",
      items: [
        {
          title: "Webové stránky",
          desc: "Moderní firemní weby a landing pages. Rychlé, čisté, postavené tak, aby šly snadno upravovat a rozšiřovat.",
          tags: ["React", "Next.js", "CMS"],
        },
        {
          title: "Webové a SaaS aplikace",
          desc: "Aplikace na míru od MVP po produkční verzi. Frontend, backend, databáze, autentizace, platby.",
          tags: ["React", "FastAPI", "Python", "MongoDB"],
        },
        {
          title: "Firemní systémy",
          desc: "Interní nástroje, klientské portály, dashboardy a CRM nadstavby. Místo Excelu reálný systém.",
          tags: ["Dashboardy", "Portály", "Interní nástroje"],
        },
        {
          title: "AI integrace",
          desc: "Praktické nasazení LLM do produktu nebo firemních procesů. Žádný hype — konkrétní úkol a měřitelný výsledek.",
          tags: ["OpenAI", "Anthropic", "Vector search", "RAG"],
        },
        {
          title: "Automatizace procesů",
          desc: "Propojení nástrojů, které dnes používáte, a automatizace opakujících se kroků. Hodiny týdně ušetřené v back-office.",
          tags: ["n8n", "Make", "REST API", "Webhooks"],
        },
        {
          title: "SEO & růst",
          desc: "Web bez návštěvníků je drahá brožura. Technické SEO, metadata, structured data, výkon, analytika, konverzní cesty a indexace jsou součástí každého projektu — ne nadstandard.",
          tags: ["Technické SEO", "Performance", "Analytika", "Schema.org"],
        },
        {
          title: "Branding & UI",
          desc: "Identita a UI systém, který doplňuje produkt — ne naopak. Design slouží srozumitelnosti, ne portfoliu.",
          tags: ["Identita", "Design system", "UI"],
        },
      ],
    },
    ai: {
      eyebrow: "AI & Automatizace",
      heading: ["AI a automatizace ", "pro reálný byznys."],
      sub: "AI a automatizace neslouží k tomu, aby byly v PR. Slouží k tomu, aby přestaly určité úkoly žrát čas vašich lidí. Tady jsou věci, které pro klienty stavíme nejčastěji.",
      items: [
        {
          title: "AI telefonní asistenti",
          desc: "Hlasoví asistenti, kteří odbaví poptávky, rezervace nebo první linku zákaznické podpory — v češtině, 24/7.",
          tags: ["Voice AI", "STT/TTS", "LLM"],
        },
        {
          title: "Systémy pro generování leadů",
          desc: "Pipelines, které sbírají, obohacují a kvalifikují leady. Obchodník dostane priority, ne tisíc kontaktů.",
          tags: ["Scraping", "Enrichment", "Scoring"],
        },
        {
          title: "Automatizace CRM",
          desc: "Propojení CRM s e-mailem, kalendářem, dokumenty a fakturací. Konec ručního přepisování dat.",
          tags: ["HubSpot", "Pipedrive", "API"],
        },
        {
          title: "Interní firemní nástroje",
          desc: "Custom dashboardy a interní aplikace, které řeší přesně to, co váš tým potřebuje — bez 30 nepotřebných funkcí.",
          tags: ["Dashboardy", "Workflow", "Reporty"],
        },
        {
          title: "AI integrace do produktu",
          desc: "Asistenti, vyhledávání, generování obsahu nebo dokumentů přímo ve vaší aplikaci. Pragmaticky, s ohledem na cenu tokenů.",
          tags: ["RAG", "Embeddings", "LLM"],
        },
        {
          title: "Workflow automatizace",
          desc: "Propojení nástrojů a procesů, které dnes klepete ručně. Méně chyb, rychlejší výsledek.",
          tags: ["n8n", "Make", "Zapier"],
        },
      ],
    },
    tech: {
      eyebrow: "Stack",
      heading: ["Technologie, ", "kterým věříme."],
      sub: "Vybrané, protože fungují. Ponechány, protože škálují.",
      note: "Nástroje vybíráme proto, aby řešily problémy — ne aby sledovaly trendy.",
      exploreLabel: "PROZKOUMAT",
      drawer: {
        powers: "K ČEMU SLOUŽÍ",
        whyWeUse: "PROČ TO POUŽÍVÁME",
        alternatives: "ZVAŽOVANÉ ALTERNATIVY",
        founder: "VYSVĚTLENÍ ZAKLADATELE",
        technical: "TECHNICKÉ DETAILY",
        close: "Zavřít",
      },
      items: [
        {
          id: "react",
          name: "React",
          tagline: "Rychlý, škálovatelný a ideální pro produkty, které mají růst.",
          powers: "Frontend rozhraní každé stránky, každé interakce, každého produktu, který stavíme.",
          whyWeUse: "Komponenty zůstávají znovupoužitelné, ekosystém se vyvíjí dál a vývojáři, které najímáme, ho už znají.",
          alternatives: "Vue (menší komunita), Angular (těžkotonážní), Svelte (méně zralé pro nábor).",
          founder: "Představte si LEGO. Nepřestavujete celý dům. Vyměníte jen ten pokoj, který chcete vylepšit.",
          technical: "React 18, hooks-first, code-splitting, lazy boundaries, server-friendly architektura.",
        },
        {
          id: "fastapi",
          name: "FastAPI",
          tagline: "Moderní Python framework. Asynchronní, typovaný, s automatickou dokumentací.",
          powers: "Každý API endpoint, autentizace, integrace, background úlohy.",
          whyWeUse: "Typově bezpečný, async-first, automaticky generovaná dokumentace, dost rychlý pro reálný provoz.",
          alternatives: "Flask (bez async), Django (těžký), Express (méně typovaný).",
          founder: "Jako kuchyně, kde má každá zásuvka popisek. Kdo přijde, hned ví, kde co je a jak to použít.",
          technical: "Python 3.12+, Pydantic v2, async I/O, OpenAPI specifikace bez konfigurace.",
        },
        {
          id: "mongodb",
          name: "MongoDB",
          tagline: "Dokumentová databáze, která roste spolu s produktem.",
          powers: "Aplikační data, uživatelské účty, obsah, analytické události.",
          whyWeUse: "Flexibilní schéma na začátku, zralá agregace, indexy a transakce na produkci.",
          alternatives: "PostgreSQL (přísné schéma, hodí se později), Firestore (vendor lock-in).",
          founder: "Jako nastavitelné police ve skříni. Jednoduše je přerovnáte, když věci rostou.",
          technical: "Replica sets, indexované dotazy, agregační pipelines, time-series tam, kde dává smysl.",
        },
        {
          id: "gsap",
          name: "GSAP",
          tagline: "Animace, které působí ručně vyrobeně, ne vygenerovaně.",
          powers: "Pohybové sekvence na landing page, scroll storytelling, herostransitions.",
          whyWeUse: "Přesná kontrola nad načasováním a easingem, výkon na každém zařízení, žádná překvapení mezi prohlížeči.",
          alternatives: "Framer Motion (skvělý na UI, méně na kinematiku), CSS animace (omezená choreografie).",
          founder: "Jako filmový střihač pracující po jednotlivých snímcích — místo aby rozhodoval auto-cut.",
          technical: "Timelines, ScrollTrigger, requestAnimationFrame, prakticky nulový jank.",
        },
        {
          id: "python",
          name: "Python",
          tagline: "Čitelný, vyzrálý a připravený na AI práci.",
          powers: "Backendová logika, datové úlohy, AI pipelines, skripty, integrace.",
          whyWeUse: "Obrovský ekosystém, AI-native knihovny, snadný nábor, čitelný i po měsících.",
          alternatives: "Node.js (méně vhodný pro AI/data), Go (strmější křivka pro non-systems práci), Rust (overkill).",
          founder: "Jako angličtina se závorkami — snadno čitelný, snadno udržovatelný, snadno předatelný.",
          technical: "Python 3.12, typové hinty, async, virtualenv, moderní balíčkování.",
        },
        {
          id: "openai",
          name: "OpenAI",
          tagline: "Spolehlivá vrstva foundation modelů pro AI funkce.",
          powers: "AI chat, generování obsahu, sumarizace, strukturovaná extrakce.",
          whyWeUse: "Nejvyšší laťka spolehlivosti a kvality dnes, předvídatelné API, dobré zacházení s rate limity.",
          alternatives: "Anthropic Claude (používáme selektivně), open-source modely (provozní režie).",
          founder: "Jako kdybyste si najali výzkumný tým, který můžete probudit ve tři ráno — rychlý, soustředěný, předvídatelný.",
          technical: "GPT modely, function calling, embeddings, streaming odpovědi.",
        },
        {
          id: "cloudflare",
          name: "Cloudflare",
          tagline: "Edge síť, bezpečnost a výkon v jedné vrstvě.",
          powers: "DNS, CDN, DDoS ochrana, edge cache, SSL.",
          whyWeUse: "Snižuje latency celosvětově, blokuje zneužití dřív, než dorazí na origin, zdarma tam, kde si konkurence účtuje.",
          alternatives: "Fastly (prémiové ceny), Akamai (enterprise pocit).",
          founder: "Představte si recepční, ostrahu a dispečera dopravy, kteří spolupracují dřív, než návštěvníci vůbec vejdou do budovy.",
          technical: "Globální anycast síť, edge rules, R2/Workers podle potřeby.",
        },
        {
          id: "stripe",
          name: "Stripe",
          tagline: "Platby postavené jako infrastruktura, ne jako plugin.",
          powers: "Jednorázové platby, předplatné, faktury, daně, refundy.",
          whyWeUse: "Špičkové developer experience, globální pokrytí, předvídatelné chování pod zátěží.",
          alternatives: "PayPal (starší UX), GoPay (lokální, ale omezený), Lemon Squeezy (nový, méně vyzrálý).",
          founder: "Pokladna, správce předplatných a účetní v jednom.",
          technical: "Stripe Checkout a Elements, webhooky, idempotentní API, EU DPH.",
        },
        {
          id: "github",
          name: "GitHub",
          tagline: "Jediný zdroj pravdy pro kód, historii a code review.",
          powers: "Verzování kódu, code review, CI/CD, issue tracking.",
          whyWeUse: "Standardní volba v oboru, integrace všude, dohledatelná historie každé změny.",
          alternatives: "GitLab (režie self-hostingu), Bitbucket (menší ekosystém).",
          founder: "Jako muzejní archiv — každá změna zaznamenaná, každá verze obnovitelná.",
          technical: "Protected branches, PR review, GitHub Actions, prostředí podle větve.",
        },
        {
          id: "figma",
          name: "Figma",
          tagline: "Design, který umí přečíst i vývojáři.",
          powers: "UI design, prototypy, designové systémy, schvalování klientem.",
          whyWeUse: "Real-time spolupráce v prohlížeči, identický zážitek pro design i vývoj.",
          alternatives: "Sketch (jen Mac, jedna osoba), Adobe XD (vývojově ustrnul).",
          founder: "Jako architektonický výkres — dost čitelný na to, aby řemeslníci přesně věděli, co dělat.",
          technical: "Auto layout, komponenty, variables, sdílené knihovny.",
        },
        {
          id: "vercel",
          name: "Vercel",
          tagline: "Nasazení, která dorazí během sekund, ne hodin.",
          powers: "Hosting frontendu, preview pro každý pull request, edge funkce.",
          whyWeUse: "Zero-config pro React/Next, instantní rollback, branch preview rovnou v základu.",
          alternatives: "Netlify (podobné), self-hosting (více ops), AWS (těžká nadstavba).",
          founder: "Jako tiskařský lis pro web — pushnete soubor a vidíte ho živě.",
          technical: "Edge runtime, ISR, optimalizace obrázků, deploys podle environmentu.",
        },
      ],
    },
    hosting: {
      eyebrow: "Hosting & vlastnictví",
      heading: ["Žádný ", "vendor lock-in."],
      sub: "Sami si vybíráte, jestli chcete mít všechno pod jednou střechou u nás, nebo si projekt po launchi převzít kompletně k sobě. Kód a infrastruktura jsou vždy vaše.",
      options: [
        {
          tag: "Volba A",
          title: "Plně spravujeme",
          desc: "Postaráme se o nasazení, hosting frontendu i backendu, doménu a dlouhodobý provoz. Vy se věnujete byznysu.",
          points: [
            "Nasazení a release management",
            "Hosting frontendu a backendu",
            "Pomoc s doménami a infrastrukturou",
            "Průběžná podpora a údržba",
          ],
        },
        {
          tag: "Volba B",
          title: "Předáme do vašich rukou",
          desc: "Po dokončení projektu předáme kompletní zdrojový kód, dokumentaci a postup pro nasazení. Projekt vlastníte vy.",
          points: [
            "Plný zdrojový kód",
            "Dokumentace a postup pro deploy",
            "Vlastnictví projektu na straně klienta",
            "Podpora během přechodu",
          ],
        },
      ],
      reassurance: "Nikdy nejste uzamčeni. Pracujeme transparentně a předáváme přesně to, na čem se domluvíme.",
    },
    moreThanWebsite: {
      eyebrow: "More than a website",
      headline: ["A website should do ", "more than look good."],
      body: "Měl by lidem pomoct vás najít, pochopit, co děláte, a stát se zákazníky.",
      items: [
        "Technické SEO",
        "Výkon",
        "Analytika",
        "Konverzní cesty",
        "Structured data",
        "Přístupnost",
        "Mobilní optimalizace",
        "Škálovatelnost",
      ],
      note: "Nevěříme v krásné weby, které nikdo nenajde.",
    },
    pricing: {
      eyebrow: "Ceník",
      heading: ["Transparentní ceny. ", "Bez překvapení."],
      sub: "Pracujeme férově. Konečnou cenu vidíte předem a víte přesně, co za ni dostanete. Konkrétní rozpočet doladíme po krátkém callu.",
      note: "Ceny jsou orientační — finální nabídku připravíme po prvním callu.",
      tiers: [
        {
          name: "Starter",
          price: "od 4 000 Kč",
          desc: "Pro malé firmy, freelancery a osobní projekty.",
          features: [
            "Jednoduchý web nebo landing page",
            "Responzivní design",
            "Základní SEO a analytika",
            "Dodání do 7 dní",
          ],
          tag: "Nejlepší pro start",
        },
        {
          name: "Standard",
          price: "od 25 000 Kč",
          desc: "Pro firmy a značky, které potřebují víc než šablonu.",
          features: [
            "Web na míru (4–8 stránek)",
            "Návrh ve Figmě před vývojem",
            "CMS pro správu obsahu",
            "Animace a mikrointerakce",
          ],
          tag: "Nejčastější volba",
          highlight: true,
        },
        {
          name: "Custom",
          price: "individuálně",
          desc: "Aplikace, SaaS produkty, AI systémy a interní nástroje.",
          features: [
            "Full-stack aplikace na míru",
            "Klientské portály a dashboardy",
            "AI integrace a automatizace",
            "Dlouhodobá technická podpora",
          ],
          tag: "Pro náročnější projekty",
        },
      ],
      cta: "Probrat projekt",
    },
    process: {
      eyebrow: "Spolupráce",
      heading: ["Jak ", "spolupracujeme."],
      sub: "Šest kroků od první zprávy po dlouhodobou podporu. Žádné PowerPointy, žádný marketingový teatr — píšete přímo se zakladatelem.",
      steps: [
        {
          n: "01",
          title: "Discover",
          desc: "Krátký úvodní call. Probereme cíl projektu, kdo ho bude používat, rozsah a termíny.",
          detail: "Call · Cíle · Rozsah",
        },
        {
          n: "02",
          title: "Design",
          desc: "Navrhneme rozhraní zaměřené na uživatele a obchodní cíl. Pracujeme ve Figmě, můžete připomínkovat průběžně.",
          detail: "Figma · UX · UI",
        },
        {
          n: "03",
          title: "Build",
          desc: "Stavíme web, aplikaci nebo AI systém. V průběhu vidíte funkční náhled, testujete a dáváte feedback.",
          detail: "React · FastAPI · MongoDB",
        },
        {
          n: "04",
          title: "Optimize",
          desc: "Technické SEO, metadata, structured data, výkon, indexace a konverzní cesty. SEO není doplněk — je součástí stavby.",
          detail: "SEO · Metadata · Analytika · Performance",
        },
        {
          n: "05",
          title: "Launch",
          desc: "Nasazení, finální testování a spuštění do produkce. Postaráme se o vše, od domény po analytiku.",
          detail: "Deploy · QA · Go-live",
        },
        {
          n: "06",
          title: "Support",
          desc: "Po launchi nezůstanete sami. Pomáháme produkt zlepšovat, ladit a postupně rozvíjet.",
          detail: "Údržba · Iterace · Long-term support",
        },
      ],
    },
    founder: {
      eyebrow: "O studiu",
      headline: ["Malé studio z Česka. ", "Bez agenturních nesmyslů."],
      paragraph:
        "Parabolum jsem založil v roce 2025 jako prostor pro klidnou a férovou práci s klienty. Bez account manažerů, bez nekonečných meetingů a bez přepálených cen. Pracujete přímo se mnou — od první zprávy po launch i dlouhodobý rozvoj. Specializuju se na moderní weby, webové aplikace, SaaS produkty a AI systémy pro firmy v Česku.",
      paragraph2:
        "Mám rád projekty, kde jde o funkční výsledek, ne o byznys teatr.",
      role: "Zakladatel & vývojář",
      name: "Jiří Mičán",
      location: "Česko",
      available: "Přijímáme nové projekty · Q3 2026",
      meta: [
        { k: "Sídlo", v: "Česko" },
        { k: "Tým", v: "Malé studio" },
        { k: "Od", v: "2025" },
      ],
    },
    finalCta: {
      badge: "Aktuálně přijímáme nové projekty",
      headline: ["Pojďme něco", "postavit."],
      sub: "Napište nám pár vět o projektu — termín, rozsah, představa. Odpovídáme do 24 hodin a první konzultace je vždy zdarma.",
      button: "Začít projekt",
      orEmail: "nebo hello@parabolum.digital",
      perks: [
        { k: "< 24 h", v: "Odpovídáme na poptávky" },
        { k: "Zdarma", v: "První konzultace" },
        { k: "Přímo", v: "Komunikace se zakladatelem" },
      ],
    },
    footer: {
      description:
        "Boutique software studio z Česka. Stavíme moderní weby, webové a SaaS aplikace a AI systémy — přímo, bez korporátu a zbytečných procesů.",
      startProject: "Začít projekt →",
      cols: [
        {
          title: "Studio",
          links: [
            { label: "Práce", href: "#work" },
            { label: "Služby", href: "#services" },
            { label: "Technologie", href: "#tech" },
            { label: "Ceník", href: "#pricing" },
            { label: "Proces", href: "#process" },
            { label: "Studio", href: "#founder" },
          ],
        },
        {
          title: "Kontakt",
          links: [
            { label: "hello@parabolum.digital", href: "mailto:hello@parabolum.digital" },
            { label: "Instagram", href: "#" },
            { label: "LinkedIn", href: "#" },
            { label: "GitHub", href: "#" },
          ],
        },
      ],
      marquee: [
        "Parabolum Studio",
        "·",
        "Weby · Aplikace · AI · Automatizace",
        "·",
        "Studio z Česka",
        "·",
        "Est. 2025",
        "·",
        "Ceny od 4 000 Kč",
        "·",
      ],
      rights: "Všechna práva vyhrazena.",
      crafted: "Vyrobeno v Česku",
      operational: "Vše funguje",
    },
    loader: {
      eyebrow: "Studio z Česka — Est. 2025",
      tagline: "Weby, aplikace a AI systémy",
      loading: "Načítání studia",
    },
    contact: {
      eyebrow: "Nový projekt",
      title: "Řekněte nám o projektu.",
      sub: "Čteme každou zprávu. Odpovídáme do 24 hodin. První konzultace zdarma.",
      labels: {
        name: "Jméno",
        email: "E-mail",
        projectType: "Typ projektu",
        budget: "Rozpočet",
        message: "Zpráva",
      },
      placeholders: {
        name: "Jan Novák",
        email: "vy@znacka.cz",
        message: "Napište nám stručně vaši představu.",
      },
      projectTypes: ["Web", "Webová aplikace", "SaaS produkt", "AI systém", "Automatizace", "Interní nástroj"],
      budgets: ["4–10 000 Kč", "10–30 000 Kč", "30–80 000 Kč", "80 000 Kč+"],
      noSpam: "Malé studio. Žádný spam, žádní obchodníci.",
      submit: "Odeslat zprávu",
      submitting: "Odesílám…",
      close: "Zavřít",
      toasts: {
        required: "Jméno, e-mail a zpráva jsou povinné",
        success: "Zpráva přijata — odpovíme do 24 hodin.",
        error: "Odeslání selhalo. Zkuste to znovu nebo nám napište na hello@parabolum.digital",
      },
    },
  },

  en: {
    nav: {
      links: [
        { label: "Work", href: "#work" },
        { label: "Services", href: "#services" },
        { label: "Tech", href: "#tech" },
        { label: "Pricing", href: "#pricing" },
        { label: "Process", href: "#process" },
        { label: "Studio", href: "#founder" },
      ],
      cta: "Start a project",
      studios: "Studios",
      langLabel: "Language",
    },
    hero: {
      eyebrow: "Studio from Czechia — since 2025",
      headlinePre: "We build",
      headlineRotating: [
        "websites",
        "web applications",
        "SaaS products",
        "AI systems",
        "automations",
      ],
      headlineLines: ["for companies that", "take business seriously."],
      sub: "A boutique software studio from Czechia. You work directly with the founder — from the first message to launch and long-term support. No account managers, no middlemen.",
      ctaPrimary: "Start a project",
      ctaSecondary: "Our work",
      scroll: "Scroll",
      floatBooking: "Currently accepting new projects · Q3 2026",
      floatStat: { value: "< 24 h", label: "average reply time" },
      pricePill: "From €160",
    },
    trust: {
      items: [
        "Based in Czechia",
        "Direct communication",
        "No outsourcing",
        "Modern technologies",
        "Long-term support",
      ],
    },
    stats: {
      eyebrow: "In short",
      heading: "Design, development and automation under one roof.",
      sub: "Direct communication, clean code and working solutions. No middlemen, no agency theatre.",
      items: [
        { value: 2025, suffix: "", label: "Founded", sub: "Studio from Czechia" },
        { value: 24, suffix: "h", label: "Reply within", sub: "Mon–Fri, email / Slack" },
        { value: 6, suffix: "", label: "Disciplines under one roof", sub: "Web · App · AI · Branding · Motion" },
        { value: 100, suffix: "%", label: "Custom, no templates", sub: "No subcontracting" },
      ],
    },
    why: {
      eyebrow: "Why work with us?",
      index: "(Parabolum® — 01)",
      annotation: "— why Parabolum.",
      items: [
        {
          n: "01",
          statement: "We build websites, apps and systems where users actually find what they came for — without friction.",
          result: "More conversions, less basic support and a digital product that genuinely works for your business.",
          outcome: "Real business impact",
        },
        {
          n: "02",
          statement: "We work with a modern technology stack and clean architecture — no templates, no shortcuts, no technical debt.",
          result: "Faster delivery of new features, lower maintenance cost and code that holds up over years.",
          outcome: "Sustainable technology",
        },
        {
          n: "03",
          statement: "You communicate directly with the founder. No middlemen, no account managers, no endless meetings.",
          result: "Faster decisions, fair pricing and a partnership where every message has a point.",
          outcome: "Direct collaboration",
        },
      ],
    },
    clients: {
      eyebrow: "Selected projects",
      headingPre: "Three projects ",
      headingItalic: "we recently",
      headingPost: " shipped and keep shipping.",
      sub: "A short selection of studio and client projects — useful if you want to see how we think about product, technology and detail.",
      logos: [
        "LeadForge",
        "Revix",
        "BitWrex",
        "Parabolum Studio",
      ],
    },
    work: {
      eyebrow: "Selected work — 2025/2026",
      heading: ["Case studies ", "from real builds", "."],
      archive: "Full archive soon",
      inMotion: "In motion",
      dashboard: "Dashboard",
      dashAmount: "€11,940.88",
      thisWeek: "+24.7% this week",
      project: "Project",
      labels: {
        challenge: "Challenge",
        solution: "Solution",
        tech: "Tech",
        outcome: "Outcome",
      },
      items: [
        {
          tag: "Lead Generation · SaaS",
          title: "LeadForge",
          year: "2026",
          desc: "B2B lead generation and qualification platform with AI-powered enrichment.",
          challenge: "Sales teams burn hours every day chasing and qualifying leads in spreadsheets and LinkedIn. Data is fragmented, often outdated, and qualification ends up being subjective.",
          solution: "We built a web platform that imports lead sources, automatically enriches them with firmographic data using AI, and ranks contacts by who's actually worth contacting first.",
          tech: ["React", "FastAPI", "MongoDB", "OpenAI API", "Vector search"],
          outcome: "Instead of manual research, sales gets a prioritised list and can focus on actual outreach. Live and in daily use.",
        },
        {
          tag: "Automotive · E-commerce",
          title: "REVIX",
          year: "2026",
          desc: "Admin dashboard and storefront for the performance / automotive segment.",
          challenge: "Performance and tuning shops still run their day between spreadsheets, marketplace listings and DM enquiries. There is no back-office that fits how this niche actually works — parts with vehicle-compatibility variants, made-to-order builds, and a customer who expects the shop to know what fits their car.",
          solution: "We built an admin dashboard and customer-facing storefront for performance automotive. A catalogue with vehicle-compatibility metadata, order and enquiry management, customer profiles and a back-office the shop owner can actually run the business from — instead of stitching together five tools.",
          tech: ["React", "FastAPI", "MongoDB", "Python", "Stripe", "Tailwind"],
          outcome: "Concept in active development. Storefront + admin in a single system, built around how a performance shop actually works.",
        },
        {
          tag: "Crypto · Exchange",
          title: "BitWrex",
          year: "2025",
          desc: "Frontend and infrastructure for a crypto exchange focused on simple, fast swaps.",
          challenge: "Most crypto exchanges are unnecessarily complicated for everyday users — order books, derivatives, leverage. The goal was an interface where a swap is three clean steps.",
          solution: "We designed and built a clean web app with a minimal swap interface, connected to a custom backend layer for pricing, KYC and transaction processing.",
          tech: ["React", "FastAPI", "Python", "MongoDB", "Stripe", "API integrations"],
          outcome: "Shipped to production with a focus on speed of UX and onboarding. Long-term development and support continues.",
        },
      ],
    },
    services: {
      eyebrow: "Services",
      heading: ["What we ", "build for you."],
      sub: "We specialise in software, AI and automation. Design and branding are part of the process, but the product always stands on real engineering.",
      items: [
        { title: "Websites", desc: "Modern marketing sites and landing pages. Fast, clean, easy to update and grow.", tags: ["React", "Next.js", "CMS"] },
        { title: "Web & SaaS applications", desc: "Custom applications from MVP to production. Frontend, backend, database, auth, payments.", tags: ["React", "FastAPI", "Python", "MongoDB"] },
        { title: "Internal systems", desc: "Internal tools, client portals, dashboards and CRM extensions. A real system instead of another spreadsheet.", tags: ["Dashboards", "Portals", "Internal tools"] },
        { title: "AI integration", desc: "Practical LLM deployments inside products and business processes. No hype — a concrete task with a measurable result.", tags: ["OpenAI", "Anthropic", "Vector search", "RAG"] },
        { title: "Process automation", desc: "Connecting the tools you already use and automating repeated steps. Hours saved every week in back-office.", tags: ["n8n", "Make", "REST API", "Webhooks"] },
        { title: "SEO & Growth", desc: "A website without visitors is just an expensive brochure. Technical SEO, metadata, structured data, performance, analytics, conversion paths and indexing are part of every project — not an extra.", tags: ["Technical SEO", "Performance", "Analytics", "Schema.org"] },
        { title: "Branding & UI", desc: "Identity and UI system that supports the product — not the other way around. Design serves clarity.", tags: ["Identity", "Design system", "UI"] },
      ],
    },
    ai: {
      eyebrow: "AI & Automation",
      heading: ["AI and automation ", "for real business."],
      sub: "AI and automation aren't there to look good in a press release. They're there so certain tasks stop eating your team's time. These are the things we build most often.",
      items: [
        {
          title: "AI phone assistants",
          desc: "Voice assistants that handle inbound enquiries, bookings or first-line support — in English or Czech, 24/7.",
          tags: ["Voice AI", "STT/TTS", "LLM"],
        },
        {
          title: "Lead generation systems",
          desc: "Pipelines that collect, enrich and qualify leads. Sales gets priorities, not a thousand cold contacts.",
          tags: ["Scraping", "Enrichment", "Scoring"],
        },
        {
          title: "CRM automation",
          desc: "Connect your CRM to email, calendar, documents and invoicing. End the manual copy-paste cycle.",
          tags: ["HubSpot", "Pipedrive", "API"],
        },
        {
          title: "Internal business tools",
          desc: "Custom dashboards and internal apps that solve exactly what your team needs — without 30 features you don't.",
          tags: ["Dashboards", "Workflow", "Reports"],
        },
        {
          title: "AI inside the product",
          desc: "Assistants, search, content or document generation built directly into your application. Pragmatic, with token cost in mind.",
          tags: ["RAG", "Embeddings", "LLM"],
        },
        {
          title: "Workflow automation",
          desc: "Connecting tools and processes you currently click through by hand. Fewer errors, faster result.",
          tags: ["n8n", "Make", "Zapier"],
        },
      ],
    },
    tech: {
      eyebrow: "Stack",
      heading: ["Technologies ", "we trust."],
      sub: "Chosen because they work. Kept because they scale.",
      note: "Tools selected to solve problems — not to follow trends.",
      exploreLabel: "EXPLORE",
      drawer: {
        powers: "WHAT IT POWERS",
        whyWeUse: "WHY WE USE IT",
        alternatives: "ALTERNATIVES CONSIDERED",
        founder: "FOUNDER EXPLANATION",
        technical: "TECHNICAL DETAILS",
        close: "Close",
      },
      items: [
        {
          id: "react",
          name: "React",
          tagline: "Fast, scalable and ideal for products expected to grow.",
          powers: "The frontend interface of every page, every interaction, every product we ship.",
          whyWeUse: "Components stay reusable, the ecosystem keeps evolving, and developers we hire already know it.",
          alternatives: "Vue (smaller community), Angular (heavy), Svelte (less mature for hiring).",
          founder: "Think LEGO. You don't rebuild the entire house. You replace only the room you want to improve.",
          technical: "React 18, hooks-first, code-splitting, lazy boundaries, server-friendly architecture.",
        },
        {
          id: "fastapi",
          name: "FastAPI",
          tagline: "Modern Python API framework. Async, typed, documented automatically.",
          powers: "Every API endpoint, authentication, integrations, background tasks.",
          whyWeUse: "Type-safe, async-first, auto-generated docs, fast enough to handle real load.",
          alternatives: "Flask (no async), Django (too heavy), Express (less typed).",
          founder: "Like a kitchen with labels on every drawer. Anyone who walks in knows where things are and how to use them.",
          technical: "Python 3.12+, Pydantic v2, async I/O, OpenAPI specs out of the box.",
        },
        {
          id: "mongodb",
          name: "MongoDB",
          tagline: "A document database that grows with the product.",
          powers: "Application data, user accounts, content, analytics events.",
          whyWeUse: "Schema flexibility for early iteration; mature aggregation, indexing and transactions in production.",
          alternatives: "PostgreSQL (rigid schema, fits later stages), Firestore (vendor lock-in).",
          founder: "Like adjustable shelves in a cabinet. Easy to reorganize as things grow.",
          technical: "Replica sets, indexed queries, aggregation pipelines, time-series where it makes sense.",
        },
        {
          id: "gsap",
          name: "GSAP",
          tagline: "Animations that feel handcrafted, not generated.",
          powers: "Motion sequences on landing pages, scroll storytelling, hero transitions.",
          whyWeUse: "Precise control over timing and easing, performant on every device, no surprises across browsers.",
          alternatives: "Framer Motion (great for UI, less for cinematic), CSS animations (limited choreography).",
          founder: "Like a film editor working frame by frame, instead of letting auto-cut decide.",
          technical: "Timelines, ScrollTrigger, requestAnimationFrame-based, near-zero jank.",
        },
        {
          id: "python",
          name: "Python",
          tagline: "Readable, mature and ready for AI work.",
          powers: "Backend logic, data tasks, AI pipelines, scripts, integrations.",
          whyWeUse: "Massive ecosystem, AI-native libraries, easy to hire for, still readable months later.",
          alternatives: "Node.js (less suited for AI/data), Go (steeper for non-systems work), Rust (overkill).",
          founder: "Like English with brackets — easy to read, easy to maintain, easy to hand over.",
          technical: "Python 3.12, type hints, async, virtualenvs, modern packaging.",
        },
        {
          id: "openai",
          name: "OpenAI",
          tagline: "Reliable foundation-model layer for AI features.",
          powers: "AI chat, content generation, summarization, structured extraction.",
          whyWeUse: "Highest reliability and quality bar today, predictable APIs, strong rate handling.",
          alternatives: "Anthropic Claude (used selectively), open-source models (operational overhead).",
          founder: "Like hiring a research team you can wake up at 3 AM — fast, focused, predictable.",
          technical: "GPT-class models, function calling, embeddings, streaming responses.",
        },
        {
          id: "cloudflare",
          name: "Cloudflare",
          tagline: "Edge network, security and performance in a single layer.",
          powers: "DNS, CDN, DDoS protection, edge caching, SSL.",
          whyWeUse: "Drops latency worldwide, blocks abuse before it hits the origin, free where most providers charge.",
          alternatives: "Fastly (premium pricing), Akamai (enterprise-only feel).",
          founder: "Imagine a receptionist, security guard and traffic controller working together before visitors even enter the building.",
          technical: "Global anycast network, edge rules, R2 and Workers when needed.",
        },
        {
          id: "stripe",
          name: "Stripe",
          tagline: "Payments built like infrastructure, not a plugin.",
          powers: "One-time payments, subscriptions, invoicing, taxes, refunds.",
          whyWeUse: "Best-in-class developer experience, global coverage, predictable behaviour under load.",
          alternatives: "PayPal (older UX), GoPay (local but limited), Lemon Squeezy (newer, less mature).",
          founder: "The cash register, subscriptions manager and accountant in one place.",
          technical: "Stripe Checkout & Elements, webhooks, idempotent APIs, EU VAT support.",
        },
        {
          id: "github",
          name: "GitHub",
          tagline: "Single source of truth for code, history and reviews.",
          powers: "Source control, code reviews, CI/CD, issue tracking.",
          whyWeUse: "Industry standard, integrations everywhere, traceable history of every change.",
          alternatives: "GitLab (self-host overhead), Bitbucket (smaller ecosystem).",
          founder: "Like a museum archive — every change recorded, every version recoverable.",
          technical: "Protected branches, PR reviews, GitHub Actions, environments per branch.",
        },
        {
          id: "figma",
          name: "Figma",
          tagline: "Design that engineers can actually read.",
          powers: "UI design, prototypes, design systems, client review.",
          whyWeUse: "Real-time collaboration in the browser, identical experience for design and engineering.",
          alternatives: "Sketch (Mac-only, single user), Adobe XD (deprecated direction).",
          founder: "Like an architect's blueprint — clear enough that the builders know exactly what to do.",
          technical: "Auto layout, components, variables, shared libraries.",
        },
        {
          id: "vercel",
          name: "Vercel",
          tagline: "Deploys that ship in seconds, not hours.",
          powers: "Frontend hosting, previews per pull request, edge functions.",
          whyWeUse: "Zero-config for React/Next, instant rollbacks, branch previews built in.",
          alternatives: "Netlify (similar), self-hosting (more ops), AWS (heavy lift).",
          founder: "Like a printing press for the web — push a file, see it live.",
          technical: "Edge runtime, ISR, image optimization, deploys per environment.",
        },
      ],
    },
    hosting: {
      eyebrow: "Hosting & ownership",
      heading: ["No ", "vendor lock-in."],
      sub: "You decide whether you want everything under one roof with us, or to take the project fully into your own hands after launch. The code and infrastructure are always yours.",
      options: [
        {
          tag: "Option A",
          title: "Fully managed",
          desc: "We handle deployment, frontend and backend hosting, domains and long-term operations. You focus on the business.",
          points: [
            "Deployment and release management",
            "Frontend and backend hosting",
            "Help with domains and infrastructure",
            "Ongoing support and maintenance",
          ],
        },
        {
          tag: "Option B",
          title: "Full handover",
          desc: "After delivery you receive complete source code, documentation and a deployment guide. The project is yours.",
          points: [
            "Full source code",
            "Documentation and deploy instructions",
            "Project owned by the client",
            "Support during the transition",
          ],
        },
      ],
      reassurance: "You're never locked in. We work transparently and hand over exactly what we agree on.",
    },
    moreThanWebsite: {
      eyebrow: "More than a website",
      headline: ["A website should do ", "more than look good."],
      body: "It should help people find you, understand what you offer, and become customers.",
      items: [
        "Technical SEO",
        "Performance",
        "Analytics",
        "Conversion paths",
        "Structured data",
        "Accessibility",
        "Mobile optimization",
        "Scalability",
      ],
      note: "We don't believe in launching beautiful websites that nobody can find.",
    },
    pricing: {
      eyebrow: "Pricing",
      heading: ["Transparent pricing. ", "No surprises."],
      sub: "We work fairly. You see the price upfront and you know exactly what you get. We fine-tune the scope after a short intro call.",
      note: "Prices are indicative — a final quote is prepared after the first call.",
      tiers: [
        {
          name: "Starter",
          price: "from €160",
          desc: "For small businesses, freelancers and personal projects.",
          features: [
            "Simple website or landing page",
            "Responsive design",
            "Basic SEO and analytics",
            "Delivery within 7 days",
          ],
          tag: "Best to start",
        },
        {
          name: "Standard",
          price: "from €1,000",
          desc: "For companies and brands that need more than a template.",
          features: [
            "Custom website (4–8 pages)",
            "Figma design before development",
            "CMS to manage content",
            "Animation and micro-interactions",
          ],
          tag: "Most common",
          highlight: true,
        },
        {
          name: "Custom",
          price: "on request",
          desc: "Applications, SaaS products, AI systems and internal tools.",
          features: [
            "Custom full-stack application",
            "Client portals and dashboards",
            "AI integration and automation",
            "Long-term technical support",
          ],
          tag: "For ambitious projects",
        },
      ],
      cta: "Discuss the project",
    },
    process: {
      eyebrow: "How we work",
      heading: ["How we ", "work together."],
      sub: "Six steps from the first message to long-term support. No PowerPoints, no marketing theatre — you talk directly to the founder.",
      steps: [
        { n: "01", title: "Discover", desc: "A short intro call. We talk about the goal of the project, who'll use it, scope and timing.", detail: "Call · Goals · Scope" },
        { n: "02", title: "Design", desc: "We design the interface around the user and the business goal. We work in Figma and you can give feedback throughout.", detail: "Figma · UX · UI" },
        { n: "03", title: "Build", desc: "We build the website, application or AI system. You see a working preview throughout and can test as we go.", detail: "React · FastAPI · MongoDB" },
        { n: "04", title: "Optimize", desc: "Technical SEO, metadata, structured data, performance, indexing and conversion review. SEO is not an extra — it's part of building a serious website.", detail: "SEO · Metadata · Analytics · Performance" },
        { n: "05", title: "Launch", desc: "Deployment, final QA and going live. We take care of everything from domain to analytics.", detail: "Deploy · QA · Go-live" },
        { n: "06", title: "Support", desc: "You're not left alone after launch. We help refine, improve and evolve the product over time.", detail: "Maintenance · Iterate · Long-term support" },
      ],
    },
    founder: {
      eyebrow: "Studio",
      headline: ["A small studio from Czechia. ", "No agency theatre."],
      paragraph:
        "I founded Parabolum in 2025 as a calm, fair place to work with clients. No account managers, no endless meetings, no inflated pricing. You work with me directly — from the first message to launch and long-term development. I specialise in modern websites, web and SaaS applications and AI systems for companies in Czechia and beyond.",
      paragraph2:
        "I like projects that focus on the actual outcome, not the business theatre.",
      role: "Founder & developer",
      name: "Jiří Mičán",
      location: "Czechia",
      available: "Accepting new projects · Q3 2026",
      meta: [
        { k: "Based", v: "Czechia" },
        { k: "Team", v: "Small studio" },
        { k: "Since", v: "2025" },
      ],
    },
    finalCta: {
      badge: "Currently accepting new projects",
      headline: ["Let's build", "something."],
      sub: "A few sentences about the project — deadline, scope, vision. We reply within 24 hours and the first consultation is always free.",
      button: "Start a project",
      orEmail: "or hello@parabolum.digital",
      perks: [
        { k: "< 24 h", v: "Average reply time" },
        { k: "Free", v: "First consultation" },
        { k: "Direct", v: "Talk to the developer" },
      ],
    },
    footer: {
      description:
        "A boutique software studio from Czechia. We build modern websites, web and SaaS apps and AI systems — directly, without the corporate overhead.",
      startProject: "Start a project →",
      cols: [
        {
          title: "Studio",
          links: [
            { label: "Work", href: "#work" },
            { label: "Services", href: "#services" },
            { label: "Tech", href: "#tech" },
            { label: "Pricing", href: "#pricing" },
            { label: "Process", href: "#process" },
            { label: "About", href: "#founder" },
          ],
        },
        {
          title: "Connect",
          links: [
            { label: "hello@parabolum.digital", href: "mailto:hello@parabolum.digital" },
            { label: "Instagram", href: "#" },
            { label: "LinkedIn", href: "#" },
            { label: "GitHub", href: "#" },
          ],
        },
      ],
      marquee: [
        "Parabolum Studio",
        "·",
        "Web · Apps · AI · Automation",
        "·",
        "Studio from Czechia",
        "·",
        "Est. 2025",
        "·",
        "From €160",
        "·",
      ],
      rights: "All rights reserved.",
      crafted: "Crafted in Czechia",
      operational: "All systems operational",
    },
    loader: {
      eyebrow: "Studio from Czechia — Est. 2025",
      tagline: "Websites, apps and AI systems",
      loading: "Loading the studio",
    },
    contact: {
      eyebrow: "New project",
      title: "Tell us about the project.",
      sub: "We read every message. We reply within 24 hours. First consultation is free.",
      labels: {
        name: "Name",
        email: "Email",
        projectType: "Project type",
        budget: "Budget",
        message: "Message",
      },
      placeholders: {
        name: "Jane Doe",
        email: "you@brand.com",
        message: "Tell us briefly what you have in mind.",
      },
      projectTypes: ["Website", "Web application", "SaaS product", "AI system", "Automation", "Internal tool"],
      budgets: ["€160 – 400", "€400 – 1.2k", "€1.2 – 3.2k", "€3.2k+"],
      noSpam: "Small studio. No spam, no sales pitches.",
      submit: "Send message",
      submitting: "Sending…",
      close: "Close",
      toasts: {
        required: "Name, email and message are required",
        success: "Message received — we'll reply within 24h.",
        error: "Couldn't send. Try again or email hello@parabolum.digital",
      },
    },
  },
};

const LangCtx = createContext({
  lang: "cs",
  setLang: () => {},
  t: dictionaries.cs,
});

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem("parabolum-lang");
      if (stored === "cs" || stored === "en") return stored;
    } catch (e) {
      /* noop */
    }
    return "cs";
  });

  useEffect(() => {
    try {
      localStorage.setItem("parabolum-lang", lang);
    } catch (e) {
      /* noop */
    }
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t: dictionaries[lang] || dictionaries.cs }),
    [lang]
  );

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() {
  return useContext(LangCtx);
}

export function useT() {
  return useContext(LangCtx).t;
}
